const BASE = '/api/auth'

export interface LoginResult {
  requires_totp: boolean
  challenge_token?: string
  access_token?: string
}

export interface MeResult {
  user_id: string
  email: string
  first_name: string | null
  last_name: string | null
  role: string
  permissions: string[]
}

export interface ProfileUpdatePayload {
  first_name?: string | null
  last_name?: string | null
  email?: string
}

async function request<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('access_token')
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers as Record<string, string> ?? {}),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${BASE}${path}`, { ...opts, headers })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    throw new ApiError(res.status, body.detail ?? 'Request failed')
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
  }
}

export const api = {
  login: (email: string, password: string) =>
    request<LoginResult>('/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  totpVerify: (challenge_token: string, code: string) =>
    request<{ access_token: string }>('/totp/verify', {
      method: 'POST',
      body: JSON.stringify({ challenge_token, code }),
    }),

  me: () => request<MeResult>('/me'),

  logout: () => request<void>('/logout', { method: 'POST' }),

  totpEnroll: () =>
    request<{ secret: string; uri: string }>('/totp/enroll', { method: 'POST' }),

  totpEnrollVerify: (secret: string, code: string) =>
    request<{ access_token: string }>('/totp/enroll/verify', {
      method: 'POST',
      body: JSON.stringify({ secret, code }),
    }),

  updateProfile: (payload: ProfileUpdatePayload) =>
    request<MeResult>('/me', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  changePassword: (current_password: string, new_password: string) =>
    request<{ status: string }>('/me/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password, new_password }),
    }),

  /** Generic methods for non-auth microservice calls. URL must be full path (e.g. /api/lead-engine/...) */
  get: <T = unknown>(url: string) => rawRequest<T>(url),
  post: <T = unknown>(url: string, body?: unknown) => rawRequest<T>(url, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch: <T = unknown>(url: string, body?: unknown) => rawRequest<T>(url, { method: 'PATCH', body: body !== undefined ? JSON.stringify(body) : undefined }),
  put: <T = unknown>(url: string, body?: unknown) => rawRequest<T>(url, { method: 'PUT', body: body !== undefined ? JSON.stringify(body) : undefined }),
  delete: <T = unknown>(url: string) => rawRequest<T>(url, { method: 'DELETE' }),
}

async function rawRequest<T>(url: string, opts: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('access_token')
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers as Record<string, string> ?? {}),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await fetch(url, { ...opts, headers })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    throw new ApiError(res.status, body.detail ?? 'Request failed')
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

/** Download a CSV from an authenticated endpoint as a file (StreamingResponse text/csv).
 *  Returns false if the request failed so callers can surface an error. */
export async function downloadCsv(url: string, filename: string): Promise<boolean> {
  const token = localStorage.getItem('access_token')
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
  if (!res.ok) return false
  const blob = await res.blob()
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.click()
  URL.revokeObjectURL(a.href)
  return true
}

export interface BulkUpdateResult {
  updated: number
  not_found: number
}

export interface ImportResult {
  inserted: number
  updated: number
  skipped: number
  total_rows: number
  errors: string[]
}

export async function importLeadsCSV(file: File): Promise<ImportResult> {
  const token = localStorage.getItem('access_token')
  const form = new FormData()
  form.append('file', file)
  const res = await fetch('/api/lead-engine/import', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    throw new ApiError(res.status, body.detail ?? 'Import failed')
  }
  return res.json()
}
